// Landing page — public marketing.
// Sections: nav, hero, problem, how it works (with mini product mockups),
// sensor teaser, social proof, pricing, footer.
// `image-slot` placeholders from the design canvas are replaced with simple
// CSS-styled divs; real photos can be swapped in later.

import { Link } from "react-router-dom";
import { AVATAR_TONES } from "../lib/avatarTones";
import { useMediaQuery } from "../lib/useMediaQuery";
import "../styles/landing.css";

const LIc = {
  drop: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5C 4.5 5, 3 6.5, 3 9 a4 4 0 0 0 8 0 c 0 -2.5 -1.5 -4 -4 -7.5z" fill="currentColor" /></svg>,
  arrow: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7H11M8 4L11 7L8 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  down: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 3V11M4 8L7 11L10 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  check: <svg width="10" height="10" viewBox="0 0 14 14" fill="none"><path d="M3 7.5L6 10L11 4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  minus: <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M3 7H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>,
  plus: <svg width="12" height="12" viewBox="0 0 14 14" fill="none"><path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>,
  spark: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2L8 6L12 7L8 8L7 12L6 8L2 7L6 6L7 2z" fill="currentColor" /></svg>,
};

function Nav() {
  return (
    <nav className="lp-nav">
      <div className="lp-nav-inner">
        <Link to="/" className="lp-wordmark">
          <img src="/logo-wordmark.png" alt="Troughmate" height={28} style={{ display: "block", width: "auto" }} />
        </Link>
        <div className="lp-nav-links">
          <a href="#how" className="lp-nav-link">How it works</a>
          <Link to="/sensor" className="lp-nav-link">Sensor</Link>
          <a href="#pricing" className="lp-nav-link">Pricing</a>
          <Link to="/login" className="lp-nav-link">Log in</Link>
          <Link to="/signup" className="lp-nav-link lp-nav-cta">Start free →</Link>
        </div>
      </div>
    </nav>
  );
}

function Hero() {
  return (
    <section className="lp-hero">
      <div className="lp-container">
        <div className="lp-hero-grid">
          <div className="lp-hero-copy">
            <span className="lp-eyebrow">
              <span className="lp-eyebrow-dot"></span>
              Early colic warning
            </span>
            <h1 className="lp-hero-headline">
              Every horse. Every <em>drink.</em> Every day.
            </h1>
            <p className="lp-hero-sub">
              Track hydration across your yard. The earliest warning sign of colic,
              days before the vet would catch it.
            </p>
            <div className="lp-hero-ctas">
              <Link to="/signup" className="lp-cta primary">
                Start free, no card needed {LIc.arrow}
              </Link>
              <a href="#how" className="lp-cta ghost">
                How it works {LIc.down}
              </a>
            </div>
            <div className="lp-hero-trustline">
              <span className="lp-hero-trustline-dot"></span>
              Free for unlimited horses · British-built · 3 beta yards live
            </div>
          </div>

          <div className="lp-hero-visual">
            <div className="lp-hero-photo">
              <img
                src="/images/horse-drinking.jpg"
                alt="A horse drinking from a calm pool of water"
                style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 20, display: "block" }}
              />
            </div>

            <div className="lp-hero-overlay">
              <div className="lp-hero-overlay-head">
                <span>Hollow Tree · Today</span>
                <span className="mono">10:42</span>
              </div>
              <div className="lp-hero-overlay-body">
                <div className="lp-mini-row">
                  <span className="avatar" style={{ background: AVATAR_TONES.bay }}>H</span>
                  <div>
                    <div className="lp-mini-name">Henry</div>
                    <div className="lp-mini-meta">4L of 50L · water down</div>
                  </div>
                  <span className="lp-mini-badge severe"><span className="dot"></span>Severe</span>
                </div>
                <div className="lp-mini-row">
                  <span className="avatar" style={{ background: AVATAR_TONES.chestnut }}>B</span>
                  <div>
                    <div className="lp-mini-name">Bella</div>
                    <div className="lp-mini-meta">14L of 45L · on pace</div>
                  </div>
                  <span className="lp-mini-badge hydrated"><span className="dot"></span>Hydrated</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Problem() {
  return (
    <section className="lp-problem" id="problem">
      <div className="lp-container">
        <header className="lp-section-head">
          <div className="lp-section-num">01 / Why this matters</div>
          <h2 className="lp-section-title">
            Colic kills more horses than anything else.<br />You're flying blind.
          </h2>
          <p className="lp-section-sub">
            Most yards measure feed in kilos and exercise in minutes. But hydration?
            It's the strongest early indicator we have, and it's the one nobody tracks.
          </p>
        </header>
        <div className="lp-card-row">
          <article className="lp-card">
            <div className="lp-card-stat">{LIc.spark} The threat</div>
            <div className="lp-card-stat-num">#1</div>
            <h3 className="lp-card-title">Colic is the leading cause of equine death in the UK.</h3>
            <p className="lp-card-body">
              An estimated 4–10% of UK horses colic each year. Severe cases need surgery; many are fatal.
            </p>
          </article>
          <article className="lp-card">
            <div className="lp-card-stat">{LIc.spark} The signal</div>
            <div className="lp-card-stat-num">48h</div>
            <h3 className="lp-card-title">Drinking drops before any other vital sign.</h3>
            <p className="lp-card-body">
              Reduced water intake often appears two days before behaviour changes, and well before pain becomes visible.
            </p>
          </article>
          <article className="lp-card">
            <div className="lp-card-stat">{LIc.spark} The gap</div>
            <div className="lp-card-stat-num">0%</div>
            <h3 className="lp-card-title">Until now, no yard system tracked it.</h3>
            <p className="lp-card-body">
              Vet records, paper diaries, livery WhatsApps. None give you a daily hydration status per horse.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
}

function MockAddHorse() {
  return (
    <div className="lp-mock">
      <div className="lp-mock-bar">
        <span className="lp-mock-bar-dot"></span>
        <span>troughmate.app/horses/new</span>
      </div>
      <div className="lp-mock-body">
        <div className="lp-mock-field">
          <span className="lp-mock-label">Name</span>
          <span className="lp-mock-input filled">Bella</span>
        </div>
        <div className="lp-mock-field">
          <span className="lp-mock-label">Weight</span>
          <span className="lp-mock-input mono focus">540 kg</span>
        </div>
        <div className="lp-mock-field">
          <span className="lp-mock-label">Breed</span>
          <span className="lp-mock-input">Welsh Cob</span>
        </div>
      </div>
    </div>
  );
}

function MockDailyLog() {
  return (
    <div className="lp-mock">
      <div className="lp-mock-bar">
        <span className="lp-mock-bar-dot"></span>
        <span>troughmate.app/log/bella</span>
      </div>
      <div className="lp-mock-body">
        <div className="lp-mock-field">
          <span className="lp-mock-label">Water drunk today</span>
          <div className="lp-mock-stepper">
            <span className="lp-mock-stepper-btn">{LIc.minus}</span>
            <span className="lp-mock-stepper-val">14<span className="unit">L</span></span>
            <span className="lp-mock-stepper-btn">{LIc.plus}</span>
          </div>
        </div>
        <div className="lp-mock-field">
          <span className="lp-mock-label">Exercise</span>
          <span className="lp-mock-input mono">45 min · hack</span>
        </div>
        <div className="lp-mock-field">
          <span className="lp-mock-label">Droppings</span>
          <span className="lp-mock-input">Normal</span>
        </div>
      </div>
    </div>
  );
}

function MockStatus() {
  return (
    <div className="lp-mock">
      <div className="lp-mock-bar">
        <span className="lp-mock-bar-dot"></span>
        <span>troughmate.app/horses/bella</span>
      </div>
      <div className="lp-mock-body">
        <div className="lp-mock-status">
          <span className="lp-mock-status-icon">{LIc.check}</span>
          <div>
            <div className="lp-mock-status-label">Hydrated</div>
            <div className="lp-mock-status-line">Bella is well hydrated.</div>
          </div>
        </div>
        <div className="lp-mock-field">
          <span className="lp-mock-label">7-day intake</span>
          <div className="lp-mock-spark">
            {[28, 36, 40, 42, 38, 44, 14].map((v, i) => (
              <span key={i} style={{ height: `${v * 0.7}%` }}></span>
            ))}
          </div>
        </div>
        <div className="lp-mock-field">
          <span className="lp-mock-label">EHE confidence</span>
          <span className="lp-mock-input mono filled" style={{ color: "var(--brand)" }}>0.94 · high</span>
        </div>
      </div>
    </div>
  );
}

function How() {
  return (
    <section className="lp-how" id="how">
      <div className="lp-container">
        <header className="lp-section-head">
          <div className="lp-section-num">02 / How it works</div>
          <h2 className="lp-section-title">Three steps. Under a minute per horse, per day.</h2>
          <p className="lp-section-sub">
            Built for the morning groom who's running late. Built for the yard owner
            who manages twelve horses across two staff rotas.
          </p>
        </header>
        <div className="lp-step-row">
          <article className="lp-step">
            <MockAddHorse />
            <span className="lp-step-num">1</span>
            <h3 className="lp-step-title">Add your horses</h3>
            <p className="lp-step-body">
              Name, weight, breed. Baseline feed gets reused as defaults so you're not
              re-entering them every day. Under a minute per horse.
            </p>
          </article>
          <article className="lp-step">
            <MockDailyLog />
            <span className="lp-step-num">2</span>
            <h3 className="lp-step-title">Log daily, in 30 seconds</h3>
            <p className="lp-step-body">
              Water in litres (eyeball is fine), exercise, feed, droppings, behaviour.
              The whole team logs against the same yard. No role setup needed.
            </p>
          </article>
          <article className="lp-step">
            <MockStatus />
            <span className="lp-step-num">3</span>
            <h3 className="lp-step-title">Get the EHE status</h3>
            <p className="lp-step-body">
              The Equine Hydration Estimator, an open algorithm from University of Exeter research, gives
              you a per-horse status from <strong>Hydrated</strong> to <strong>Severe</strong>, with reasoning shown.
            </p>
          </article>
        </div>
      </div>
    </section>
  );
}

function SensorTeaser() {
  return (
    <section className="lp-sensor" id="sensor">
      <div className="lp-container">
        <div className="lp-sensor-card">
          <div className="lp-sensor-copy">
            <span className="lp-eyebrow on-dark">
              <span className="lp-eyebrow-dot"></span>
              Coming soon · waitlist open
            </span>
            <h2 className="lp-sensor-headline">Hydration, on autopilot.</h2>
            <p className="lp-sensor-sub">
              The Troughmate sensor measures intake per horse, automatically.
              Same dashboard. No daily logging. Early-access pricing for waitlist yards.
            </p>
            <div className="lp-sensor-ctas">
              <Link to="/sensor" className="lp-cta invert">Join the waitlist {LIc.arrow}</Link>
              <Link
                to="/sensor"
                className="lp-cta ghost"
                style={{ color: "#FFF", borderColor: "rgba(255,255,255,0.30)" }}
              >
                See the sensor
              </Link>
            </div>
          </div>
          <div className="lp-sensor-viz">
            <span className="lp-sensor-tag">Troughmate sensor</span>
            <span className="lp-sensor-ping">live · 0.4 L/min</span>
            <div className="lp-sensor-trough">
              <div className="lp-sensor-water"></div>
            </div>
            <div className="lp-sensor-device">
              <span className="lp-sensor-device-led"></span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Quote() {
  return (
    <section className="lp-quote">
      <div className="lp-container">
        <div className="lp-quote-grid">
          <div className="lp-quote-photo">
            <img
              src="/images/yard-manager.png"
              alt="Yard manager being nuzzled by a horse in a field"
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 16, display: "block" }}
            />
          </div>
          <div>
            <p className="lp-quote-text">
              We spotted Henry going off his water on a Sunday morning. Vet caught
              an early impaction Tuesday. Without this we'd have missed it for days.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  const appFeatures = [
    "Unlimited horses",
    "Unlimited team members",
    "Daily log: water, feed, exercise, droppings, behaviour",
    "EHE hydration status per horse",
    "30-day timeline + trends",
    "Email notifications when a horse goes Severe",
  ];
  const sensorFeatures = [
    "Per-horse intake, automatic",
    "No daily logging required",
    "Plugs into the same dashboard",
    "Early-access pricing for waitlist yards",
  ];
  return (
    <section className="lp-pricing" id="pricing">
      <div className="lp-container">
        <header className="lp-section-head">
          <div className="lp-section-num">04 / Pricing</div>
          <h2 className="lp-section-title">Free during the beta.</h2>
          <p className="lp-section-sub">
            The app is free for now. Log as many horses as you like, with as many teammates as you like.
            The Troughmate sensor will be the paid upgrade when it ships.
          </p>
        </header>
        <div className="lp-price-row">
          <article className="lp-price featured">
            <div className="lp-price-tag-row">
              <span className="lp-price-name">The app</span>
              <span className="lp-eyebrow">Available now</span>
            </div>
            <div className="lp-price-amount">
              <span className="lp-price-amount-num">£0</span>
              <span className="lp-price-amount-unit">/ for now</span>
            </div>
            <p className="lp-price-body">
              Everything you need to track hydration on your yard. No card required.
            </p>
            <ul className="lp-price-features">
              {appFeatures.map((f) => (
                <li key={f} className="lp-price-feat">
                  <span className="lp-price-feat-check">{LIc.check}</span>
                  {f}
                </li>
              ))}
            </ul>
            <Link to="/signup" className="lp-cta primary" style={{ width: "100%", justifyContent: "center" }}>
              Start free, no card needed {LIc.arrow}
            </Link>
          </article>

          <article className="lp-price">
            <div className="lp-price-tag-row">
              <span className="lp-price-name">The sensor</span>
              <span className="lp-eyebrow">Coming soon</span>
            </div>
            <div className="lp-price-amount">
              <span className="lp-price-amount-tbd">Pricing coming soon</span>
            </div>
            <p className="lp-price-body">
              The Troughmate sensor takes the daily logging away entirely. Join the waitlist for early-access pricing.
            </p>
            <ul className="lp-price-features">
              {sensorFeatures.map((f) => (
                <li key={f} className="lp-price-feat">
                  <span className="lp-price-feat-check">{LIc.check}</span>
                  {f}
                </li>
              ))}
            </ul>
            <Link to="/sensor" className="lp-cta ghost" style={{ width: "100%", justifyContent: "center" }}>
              Join the waitlist {LIc.arrow}
            </Link>
          </article>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="lp-footer">
      <div className="lp-container">
        <div className="lp-footer-grid">
          <Link to="/" className="lp-wordmark">
            <img src="/logo-wordmark.png" alt="Troughmate" height={24} style={{ display: "block", width: "auto" }} />
          </Link>
          <div className="lp-footer-meta">
            <a href="#about">About</a>
            <a href="mailto:hello@troughmate.com">Contact</a>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
        </div>
        <div className="lp-footer-fineprint">
          © 2026 Troughmate, What Does The Horse Drink Ltd. Built in the UK.
          Built on the Equine Hydration Estimator algorithm from the University of Exeter.
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  const isMobile = useMediaQuery("(max-width: 600px)");
  return (
    <div className={`lp ${isMobile ? "is-mobile" : "is-desktop"}`}>
      <Nav />
      <Hero />
      <Problem />
      <How />
      <SensorTeaser />
      <Quote />
      <Pricing />
      <Footer />
    </div>
  );
}
